import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = '/api';

function UserSubscriptionModal({ userId, user, onClose, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Форма создания подписки
  const [selectedPlan, setSelectedPlan] = useState('');
  const [durationDays, setDurationDays] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  
  // Форма продления
  const [extendDays, setExtendDays] = useState(30);
  const [extendingId, setExtendingId] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchData();
    }
  }, [userId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');

      const [subsResponse, plansResponse] = await Promise.all([
        axios.get(`${API_URL}/admin/users/${userId}/subscriptions`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/admin/plans`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setSubscriptions(subsResponse.data.subscriptions || []);
      setPlans(plansResponse.data.plans || []);
      
      if (plansResponse.data.plans && plansResponse.data.plans.length > 0) {
        setSelectedPlan(plansResponse.data.plans[0].plan_code);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async (e) => {
    e.preventDefault();
    
    if (!selectedPlan) {
      setError('Выберите план подписки');
      return;
    }

    // Проверяем и устанавливаем дефолтное значение если поле пустое
    const days = durationDays === '' || durationDays < 1 ? 30 : durationDays;

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);
      const token = localStorage.getItem('admin_token');

      await axios.post(
        `${API_URL}/admin/users/${userId}/subscription`,
        { planCode: selectedPlan, durationDays: days },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage('Подписка успешно назначена');
      setDurationDays(30); // Сбрасываем на дефолт
      await fetchData();
      if (onUpdate) onUpdate();
      
      // Очистить сообщение через 3 секунды
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to create subscription:', err);
      setError(err.response?.data?.error || 'Не удалось создать подписку');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExtendSubscription = async (subscriptionId) => {
    try {
      setExtendingId(subscriptionId);
      setError(null);
      setSuccessMessage(null);
      const token = localStorage.getItem('admin_token');

      await axios.patch(
        `${API_URL}/admin/subscriptions/${subscriptionId}/extend`,
        { additionalDays: extendDays },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(`Подписка продлена на ${extendDays} дней`);
      await fetchData();
      if (onUpdate) onUpdate();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to extend subscription:', err);
      setError(err.response?.data?.error || 'Не удалось продлить подписку');
    } finally {
      setExtendingId(null);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!confirm('Вы уверены, что хотите отменить эту подписку?')) {
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      const token = localStorage.getItem('admin_token');

      await axios.patch(
        `${API_URL}/admin/subscriptions/${subscriptionId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage('Подписка успешно отменена');
      await fetchData();
      if (onUpdate) onUpdate();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      setError(err.response?.data?.error || 'Не удалось отменить подписку');
    }
  };

  const getActiveSubscription = () => {
    return subscriptions.find(
      (sub) => sub.status === 'active' && new Date(sub.expires_at) > new Date()
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDaysRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const activeSubscription = getActiveSubscription();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Управление подписками</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="admin-loader"></div>
            </div>
          ) : (
            <>
              {/* Информация о пользователе */}
              <div className="user-info-section">
                <h3>Пользователь</h3>
                <div className="user-info-grid">
                  <div>
                    <span className="info-label">Имя:</span>
                    <span className="info-value">
                      {user?.first_name} {user?.last_name}
                    </span>
                  </div>
                  <div>
                    <span className="info-label">Username:</span>
                    <span className="info-value">@{user?.username || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="info-label">Telegram ID:</span>
                    <span className="info-value">{user?.telegram_id}</span>
                  </div>
                  <div>
                    <span className="info-label">Документов подписано:</span>
                    <span className="info-value">{user?.documents_signed_count || 0}</span>
                  </div>
                </div>
              </div>

              {/* Сообщения */}
              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="alert alert-success">
                  {successMessage}
                </div>
              )}

              {/* Активная подписка */}
              {activeSubscription && (
                <div className="active-subscription-section">
                  <h3>Текущая активная подписка</h3>
                  <div className="subscription-card active">
                    <div className="subscription-info">
                      <div className="subscription-type">
                        <span className="badge badge-success">{activeSubscription.type}</span>
                      </div>
                      <div className="subscription-details">
                        <div>
                          <span className="detail-label">Использовано документов:</span>
                          <span className="detail-value">
                            {activeSubscription.documents_used} / {activeSubscription.documents_limit}
                          </span>
                        </div>
                        <div>
                          <span className="detail-label">Истекает:</span>
                          <span className="detail-value">
                            {formatDate(activeSubscription.expires_at)}
                            <span className="text-gray-500 ml-2">
                              (через {getDaysRemaining(activeSubscription.expires_at)} дн.)
                            </span>
                          </span>
                        </div>
                        <div>
                          <span className="detail-label">Создана:</span>
                          <span className="detail-value">
                            {formatDate(activeSubscription.started_at)}
                          </span>
                        </div>
                        {activeSubscription.payment_provider && (
                          <div>
                            <span className="detail-label">Источник:</span>
                            <span className="detail-value">{activeSubscription.payment_provider}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="subscription-actions">
                      <div className="extend-form">
                        <input
                          type="number"
                          min="1"
                          value={extendDays}
                          onChange={(e) => setExtendDays(parseInt(e.target.value) || 30)}
                          className="form-input small"
                          placeholder="Дней"
                        />
                        <button
                          onClick={() => handleExtendSubscription(activeSubscription.id)}
                          disabled={extendingId === activeSubscription.id}
                          className="admin-button admin-button-primary small"
                        >
                          {extendingId === activeSubscription.id ? 'Продление...' : 'Продлить'}
                        </button>
                      </div>
                      <button
                        onClick={() => handleCancelSubscription(activeSubscription.id)}
                        className="admin-button admin-button-danger small"
                      >
                        Отменить
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Форма назначения новой подписки */}
              <div className="create-subscription-section">
                <h3>Назначить новую подписку</h3>
                <form onSubmit={handleCreateSubscription} className="subscription-form">
                  <div className="form-group">
                    <label htmlFor="plan">Тарифный план</label>
                    <select
                      id="plan"
                      value={selectedPlan}
                      onChange={(e) => setSelectedPlan(e.target.value)}
                      className="form-select"
                      required
                    >
                      {plans.map((plan) => (
                        <option key={plan.id} value={plan.plan_code}>
                          {plan.name} ({plan.documents_limit} документов, {plan.price} {plan.currency})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="duration">Длительность (дней)</label>
                    <input
                      type="number"
                      id="duration"
                      min="1"
                      value={durationDays}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          setDurationDays('');
                        } else {
                          const num = parseInt(val);
                          if (!isNaN(num) && num > 0) {
                            setDurationDays(num);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        // При потере фокуса, если поле пустое, ставим 30
                        if (e.target.value === '') {
                          setDurationDays(30);
                        }
                      }}
                      className="form-input"
                      placeholder="30"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="admin-button admin-button-primary"
                  >
                    {submitting ? 'Создание...' : 'Назначить подписку'}
                  </button>
                </form>
              </div>

              {/* История подписок */}
              <div className="subscription-history-section">
                <h3>История подписок</h3>
                {subscriptions.length === 0 ? (
                  <p className="text-gray-500">Подписок не найдено</p>
                ) : (
                  <div className="history-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Тип</th>
                          <th>Статус</th>
                          <th>Документов</th>
                          <th>Создана</th>
                          <th>Истекает</th>
                          <th>Источник</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptions.map((sub) => (
                          <tr key={sub.id}>
                            <td>{sub.type}</td>
                            <td>
                              <span
                                className={`badge ${
                                  sub.status === 'active'
                                    ? 'badge-success'
                                    : sub.status === 'expired'
                                    ? 'badge-gray'
                                    : 'badge-danger'
                                }`}
                              >
                                {sub.status}
                              </span>
                            </td>
                            <td>
                              {sub.documents_used} / {sub.documents_limit}
                            </td>
                            <td>
                              {new Date(sub.started_at).toLocaleDateString('ru-RU')}
                            </td>
                            <td>
                              {new Date(sub.expires_at).toLocaleDateString('ru-RU')}
                            </td>
                            <td>{sub.payment_provider || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserSubscriptionModal;

